import { CC_BOOKING_URL, ELATION_BOOKING_URL, NP_BOOKING_URL } from '@/config/medical-constants';

// The two kinds of Elation visit (spec 2026-10-26-recurring-virtual-visits §4.11). WF69 stamps the kind from the
// appointment type; a stamp without one is older, or was written with the switch off, and names no kind.
export type VisitKind = 'cc' | 'np';

export const VISIT_KINDS: readonly VisitKind[] = ['cc', 'np'];

export const VISIT_KIND_PERSON: Record<VisitKind, string> = { cc: 'Care Coordinator', np: 'Nurse Practitioner' };

export const VISIT_KIND_NAME: Record<VisitKind, string> = { cc: 'Welcome Call', np: 'Virtual Visit' };

// Staff wording ("Hold the NP visit"), never shown to a patient.
export const VISIT_KIND_SHORT: Record<VisitKind, string> = { cc: 'coordinator visit', np: 'NP visit' };

export const INITIAL_VISIT_CODE = 'onb_virtual_visit';
export const STAFF_VISIT_CODES: readonly string[] = [INITIAL_VISIT_CODE, 'cc_virtual_visit', 'np_virtual_visit'];
export const PATIENT_VISIT_CARD_CODE = 'onb_patient_virtual_visit';

const BOOKING_URL: Record<VisitKind, string> = { cc: CC_BOOKING_URL, np: NP_BOOKING_URL };

export function isVisitKind(v: unknown): v is VisitKind {
  return v === 'cc' || v === 'np';
}

/** The kind on a WF69 stamp (`context_json.appointment`), or null for a stamp that names none. */
export function visitKindOf(stamp: unknown): VisitKind | null {
  if (!stamp || typeof stamp !== 'object' || Array.isArray(stamp)) return null;
  const k = (stamp as { kind?: unknown }).kind;
  return isVisitKind(k) ? k : null;
}

/** A visit card's title as the patient's app shows it: the stamp's kind wins over the stored title, which an adopted
 *  pre-change card keeps ("Your Virtual Visit"). Mirrors the companion's taskDisplayTitle. */
export function patientCardTitle(
  task: { title?: unknown; context_json?: unknown } | null | undefined,
  code: string | null | undefined,
): string {
  if (!task) return '';
  if (code === PATIENT_VISIT_CARD_CODE) {
    let ctx = task.context_json;
    if (typeof ctx === 'string') { try { ctx = JSON.parse(ctx); } catch { ctx = null; } }
    const k = visitKindOf(ctx && typeof ctx === 'object' ? (ctx as { appointment?: unknown }).appointment : null);
    if (k) return `Your ${VISIT_KIND_NAME[k]} with your ${VISIT_KIND_PERSON[k]}`;
  }
  return task.title == null ? '' : String(task.title);
}

/** That kind's booking link; the general page when there is no kind (an older card). */
export function bookingUrlFor(kind: VisitKind | null | undefined): string {
  return kind ? BOOKING_URL[kind] : ELATION_BOOKING_URL;
}

/** The request message WF33 sends (Build Chat Event), echoed in the chat before the log catches up. The kind is
 *  named only when the message is not also texted: a carrier channel never says who the visit is with. */
export function visitRequestMessage(firstName: string | null | undefined, kind: VisitKind | null, texted: boolean): string {
  const name = firstName || 'there';
  const url = bookingUrlFor(kind);
  return kind && !texted
    ? `Hi ${name}, your ${VISIT_KIND_PERSON[kind]} would like to see you. Please book a visit here: ${url} - we'll confirm as soon as you've picked a slot.`
    : `Hi ${name}, your care team is ready to see you. Please book an appointment here: ${url} - we'll confirm as soon as you've picked a slot.`;
}
